import { Location, LocationDescriptor } from 'history';
import { BZ2_FACET_LOCATION, BZ2_SHOW_PLACES_RADIUS, LOCATION_FACET_COUNTY } from 'mk/autogenerated/translations/GridPageLogic.a0cb9c72a5514d4b7fd50eef68aa500f'
import { FACET_NAMES } from 'mk/bazaar/common/enums';
import { Filter } from 'mk/bazaar/product/filter/filterUtils';
import { CategoryKey } from 'mk2/apps/bazaar/components/CategoryFacetOption';
import CategorySelect from 'mk2/apps/bazaar/components/CategorySelect';
import { FacetsData } from 'mk2/apps/bazaar/components/FacetsFilter';
import { OptionKey } from 'mk2/apps/bazaar/components/FacetDropdownOption';
import FacetSelect, { PropertyOption } from 'mk2/apps/bazaar/components/FacetSelect';
import { PriceType } from 'mk2/apps/bazaar/components/PriceFacetDropdown';
import PriceSelect, { PRICE_SELECT_FORM_NAME } from 'mk2/apps/bazaar/components/PriceSelect';
import PropertyListItem from 'mk2/apps/bazaar/components/PropertyListItem';
import { NavigationHistoryState } from 'mk2/apps/bazaar/helpers';
import { hashFilter } from 'mk2/apps/bazaar/helpers/filter';
import { ScrollPosition } from 'mk2/apps/bazaar/reducers/navigation';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { cacheLast } from 'mk2/helpers/cache';
import { cachedInitialValues } from 'mk2/helpers/form';
import { getSessionStorage } from 'mk2/helpers/storages';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { parse } from 'query-string';
import React from 'react';
import styles from './GridPageLogic.mscss';

const pBrandAlwaysDisplayed = ['OTHER_BRAND'];
const pBrandModelAlwaysDisplayed = ['OTHER_BRAND_MODEL'];
const pManufacturerAlwaysDisplayed = ['OTHER_BRAND'];
const pFitsOnModelAlwaysDisplayed = ['OTHER_BRAND_MODEL', 'UNIVERSAL_BRAND_MODEL'];
const LOCATION_PREFIX = 'location_';
const PLACE_PREFIX = 'place_';
const PLACES_RADIUS_KEY = 'places_radius';
const locationsAlwaysDisplayed = [PLACES_RADIUS_KEY];

interface OwnProps {
    className?: string;
    filter: Filter;
    facets: FacetsData;
    loadingState: LoadingState;
    isMobile: boolean;
    openedFacet: string;

    location: Location<NavigationHistoryState>;

    scroll: ScrollPosition;

    onLoad();
    generateCategoryTo(category: CategoryKey): LocationDescriptor;
    onFacetChange(facetKey: string, optionKey: OptionKey, checked: boolean);
    onPriceChange(priceType: PriceType, value: number);
    onPlacesRadiusChange(enabled: boolean);
    onNavigationStoreBack(back: Location, scroll: ScrollPosition);
    onNavigationActivate(hash: string);
}

interface State {
    loadingState: LoadingState;
}

type Props = OwnProps;

class GridPageLogic extends React.Component<Props, State> {
    private cacheFacetOptions = cacheLast<PropertyOption[]>(true);

    public componentDidMount() {
        const { location, scroll } = this.props;
        if (location.state && location.state.fromProductDetail && scroll) {
            window.scrollTo(scroll.x, scroll.y);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.filter !== this.props.filter) {
            const { page: prevPage, start: prevStart, ...prevFilterWithoutPaging } = prevProps.filter;
            const { page, start, ...filterWithoutPaging } = this.props.filter;
            // Trigger only if hash of filter has changed
            if (hashFilter(prevFilterWithoutPaging) !== hashFilter(filterWithoutPaging)) {
                this.props.onNavigationActivate(hashFilter(filterWithoutPaging));
            }
        }

        // We are closing facet - scroll back to last
        // know position stored when facet was opened
        if (prevProps.openedFacet && !this.props.openedFacet) {
            const scroll = getSessionStorage().getItem('useGrid__scroll');
            const y = scroll ? parseFloat(scroll) : null;
            if (y) {
                window.scrollTo(0, y);
            }
        }
    }

    public componentWillUnmount() {
        const { onNavigationStoreBack, location } = this.props;
        onNavigationStoreBack(location, { x: window.scrollX, y: window.scrollY });
    }

    public render() {
        const { loadingState, isMobile, facets, children, openedFacet, filter, onPriceChange } = this.props;

        // Show categories list for mobile
        let sub: React.ReactNode = null;
        if (openedFacet === 'cats' && isMobile) {
            sub = (
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.onLoad}
                    onRenderSuccess={this.renderCategoriesSuccess}
                    hasData={facets?.cats?.length > 0}
                />
            );
        } else if (openedFacet === 'price' && isMobile) {
            sub = (
                <PriceSelect
                    initialValues={cachedInitialValues(
                        PRICE_SELECT_FORM_NAME,
                        {
                            min: filter.price_lo,
                            max: filter.price_hi,
                        },
                        filter.price_lo,
                        filter.price_hi,
                    )}
                    onChange={onPriceChange}
                />
            );
        } else if (openedFacet && isMobile) {
            sub = (
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.onLoad}
                    onRenderSuccess={this.renderFacetSuccess}
                    hasData={facets?.cats?.length > 0}
                />
            );
        }

        return sub || children;
        // TODO: Alternative (more performant) without unmounting children
        //  - but will refresh ads under display: none element
        // return (
        //     <>
        //         {sub}
        //         <div className={cx(styles.GridPanelLogic__children, sub && styles['GridPanelLogic__children--hidden'])}>
        //             {children}
        //         </div>
        //     </>
        // );
    }

    private renderCategoriesSuccess = () => {
        const { className, location, generateCategoryTo, facets, filter } = this.props;
        const qs = parse(location.search) || {};
        const selectedParentCategory = (qs['parent-category'] as string) || null;

        return (
            <CategorySelect
                className={className}
                parentCategory={selectedParentCategory}
                facets={facets}
                selected={filter.cats.length ? filter.cats[0] : null}
                generateCategoryTo={generateCategoryTo}
            />
        );
    };

    private renderFacetSuccess = () => {
        const { facets, filter, openedFacet, isMobile } = this.props;

        const options = this.cacheFacetOptions(() => {
            let opts =
                facets[openedFacet]?.reduce<PropertyOption[]>(
                    (acc, cur) => [
                        ...acc,
                        {
                            value: cur[0],
                            label: (cur.length === 2 ? cur[0] : cur[1]).toString(),
                            filterValue: (cur.length === 2 ? cur[0] : cur[1]).toString(),
                            count: cur.length === 2 ? cur[1] : cur[2],
                        },
                    ],
                    [],
                ) ?? [];

            if (openedFacet === 'locations') {
                opts = [
                    ...opts.map((o) => ({
                        ...o,
                        value: `${LOCATION_PREFIX}${o.value}`,
                        label: `${o.label} (${LOCATION_FACET_COUNTY})`,
                        filterValue: `${o.label} (${LOCATION_FACET_COUNTY})`,
                    })),
                    ...(facets.places || []).reduce<PropertyOption[]>(
                        (acc, cur) => [
                            ...acc,
                            {
                                value: `${PLACE_PREFIX}${cur[0]}`,
                                label: (cur.length === 2 ? cur[0] : cur[1]).toString(),
                                filterValue: (cur.length === 2 ? cur[0] : cur[1]).toString(),
                                count: cur.length === 2 ? cur[1] : cur[2],
                            },
                        ],
                        [],
                    ),
                ];
            }

            return opts;
        }, [facets, openedFacet, filter]);
        const hasFiltering = options.length > 10;

        return (
            <FacetSelect
                hasFiltering={hasFiltering}
                options={options}
                isMobile={isMobile}
                label={
                    openedFacet === 'locations'
                        ? BZ2_FACET_LOCATION
                        : FACET_NAMES[`BZ2_FACET_${openedFacet.toUpperCase()}`] ||
                          `BZ2_FACET_${openedFacet.toUpperCase()}`
                }
                property={openedFacet}
                isSelected={this.isSelected}
                onItemClick={this.onPropertyClick}
                alwaysDisplayed={
                    openedFacet === 'p_brand'
                        ? pBrandAlwaysDisplayed
                        : openedFacet === 'p_brand_model' || openedFacet === 'p_brand_models'
                        ? pBrandModelAlwaysDisplayed
                        : openedFacet === 'locations'
                        ? locationsAlwaysDisplayed
                        : openedFacet === 'p_manufacturer'
                        ? pManufacturerAlwaysDisplayed
                        : openedFacet === 'p_fits_on_model'
                        ? pFitsOnModelAlwaysDisplayed
                        : null
                }
            >
                {openedFacet === 'locations' && (
                    <PropertyListItem
                        className={styles.GridPageLogic__locations__placesRadius}
                        isLast
                        onClick={this.onPlacesRadiusClick}
                        property="placesRadius"
                        value={PLACES_RADIUS_KEY}
                        label={BZ2_SHOW_PLACES_RADIUS}
                        selected={filter.settings.placesRadius}
                    />
                )}
            </FacetSelect>
        );
    };

    private isSelected = (opt: PropertyOption): boolean => {
        const { openedFacet, filter } = this.props;
        if (openedFacet === 'locations') {
            if (opt.value.toString().startsWith(LOCATION_PREFIX)) {
                return filter.locations.includes(parseInt(opt.value.toString().substr(LOCATION_PREFIX.length), 10));
            } else if (opt.value.toString().startsWith(PLACE_PREFIX)) {
                return filter.places.includes(parseInt(opt.value.toString().substr(PLACE_PREFIX.length), 10));
            }

            return false;
        } else {
            return filter.props[openedFacet]?.includes(opt.value as string);
        }
    };

    private onPropertyClick = (property: string, value: string) => {
        const { filter, onFacetChange } = this.props;
        if (property === 'locations') {
            if (value.startsWith(LOCATION_PREFIX)) {
                const val = parseInt(value.substr(LOCATION_PREFIX.length), 10);
                onFacetChange('locations', val, !filter.locations?.includes(val));
            } else if (value.startsWith(PLACE_PREFIX)) {
                const val = parseInt(value.substr(PLACE_PREFIX.length), 10);
                onFacetChange('places', val, !filter.places?.includes(val));
            }
        } else {
            onFacetChange(property, value, !filter.props[property]?.includes(value));
        }
    };

    private onPlacesRadiusClick = () => {
        const { filter, onPlacesRadiusChange } = this.props;
        onPlacesRadiusChange(!filter.settings.placesRadius);
    };

    private onLoad = () => {
        const { onLoad, loadingState } = this.props;
        if (!isSuccess(loadingState)) {
            onLoad();
        }
    };
}

export default GridPageLogic;
